import { UpsertRightRoleDto } from "@ac-expo/api-types";
import { privateAxios } from "../axiosInstances";
import { isAxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import rightsQueryKeys from "../query-keys/rights.query-keys";
import { AcExpoRightsForm, RightsForm } from "../schemas";

const upsertRightRole = async (
  body: UpsertRightRoleDto,
  asAcExpo?: boolean
) => {
  try {
    if (body.rightRoleId) {
      const url = asAcExpo ? "/rights/ac-expo/role" : "/rights/role";

      const { data } = await privateAxios.patch(
        `${url}/${body.rightRoleId}`,
        body
      );
      return data;
    }

    const url = asAcExpo ? "/rights/ac-expo" : "/rights";

    const { data } = await privateAxios.post(url, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpsertRightRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      body,
      asAcExpo,
    }: {
      body: UpsertRightRoleDto;
      asAcExpo?: boolean;
    }) => upsertRightRole(body, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: rightsQueryKeys.list(asAcExpo),
      });
    },
  });
};

const updateRightRole = async (
  id: string,
  body: RightsForm | AcExpoRightsForm,
  asAcExpo?: boolean
) => {
  const url = asAcExpo ? "/rights/ac-expo" : "/rights";

  try {
    const { data } = await privateAxios.patch(`${url}/${id}`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpdateRightRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
      asAcExpo,
    }: {
      id: string;
      body: RightsForm | AcExpoRightsForm;
      asAcExpo?: boolean;
    }) => updateRightRole(id, body, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: rightsQueryKeys.list(asAcExpo),
      });
    },
  });
};

const deleteRightRole = async (id: string, asAcExpo?: boolean) => {
  const url = asAcExpo ? `/rights/ac-expo` : `/rights`;

  const { data } = await privateAxios.delete(`${url}/${id}`);
  return data;
};

export const useDeleteRightRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, asAcExpo }: { id: string; asAcExpo?: boolean }) =>
      deleteRightRole(id, asAcExpo),
    onSuccess: (_data, { asAcExpo }) => {
      queryClient.invalidateQueries({
        queryKey: rightsQueryKeys.list(asAcExpo),
      });
    },
  });
};
